import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { UserCheck, ArrowUpRight, Timer, AlertCircle } from 'lucide-react';
import { ticketsAPI } from '../../services/api';

/**
 * Co-managed IT: the panel a client's own technician works a ticket from.
 *
 * Shown on the customer ticket page when the company is co-managed and the
 * user holds "work", "escalate" or "log time" at that company. Everything
 * it offers comes from one call (`/tickets/:id/work`), so what it shows is
 * exactly what the server will accept: the teammates a ticket can be given
 * to, the statuses the support provider allows, and whether the ticket is
 * still on the team's board or already escalated.
 *
 * No vendor is named: "your support provider" is who the escalation goes to.
 */
export default function ClientWorkPanel({ ticketId, closed, onChanged }) {
  const queryClient = useQueryClient();
  const [error, setError] = useState(null);
  const [reason, setReason] = useState('');
  const [showEscalate, setShowEscalate] = useState(false);
  const [hours, setHours] = useState('');
  const [timeNote, setTimeNote] = useState('');
  const [showTime, setShowTime] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ['ticket', ticketId, 'work'],
    queryFn: async () => (await ticketsAPI.work(ticketId)).data,
  });

  const refresh = () => {
    queryClient.invalidateQueries({ queryKey: ['ticket', ticketId] });
    onChanged?.();
  };
  const fail = (err, fallback) => setError(err.response?.data?.error || fallback);

  const assign = useMutation({
    mutationFn: (portalUserId) => ticketsAPI.setAssignee(ticketId, portalUserId),
    onSuccess: refresh,
    onError: (err) => fail(err, 'Could not change the assignee'),
  });
  const status = useMutation({
    mutationFn: (statusId) => ticketsAPI.setStatus(ticketId, statusId),
    onSuccess: refresh,
    onError: (err) => fail(err, 'Could not change the status'),
  });
  const escalate = useMutation({
    mutationFn: () => ticketsAPI.escalate(ticketId, reason.trim()),
    onSuccess: () => {
      setShowEscalate(false);
      setReason('');
      refresh();
    },
    onError: (err) => fail(err, 'Could not escalate the ticket'),
  });
  const logTime = useMutation({
    mutationFn: () => ticketsAPI.logTime(ticketId, { hours: Number(hours), notes: timeNote.trim() }),
    onSuccess: () => {
      setShowTime(false);
      setHours('');
      setTimeNote('');
      refresh();
    },
    onError: (err) => fail(err, 'Could not log the time'),
  });

  if (isLoading || !data?.comanaged) return null;
  if (!data.canWork && !data.canEscalate && !data.canLogTime) return null;

  const busy = assign.isPending || status.isPending || escalate.isPending || logTime.isPending;

  return (
    <div className="card mb-6">
      <div className="card-header flex items-center justify-between">
        <h2 className="text-lg font-semibold text-neutral-900 dark:text-white flex items-center gap-2">
          <UserCheck className="w-5 h-5" /> Work this ticket
        </h2>
        {data.escalated && (
          <span className="badge bg-purple-100 text-purple-700 dark:bg-purple-900/50 dark:text-purple-300" title={data.lastHandoff?.reason || ''}>
            With your support provider{data.lastHandoff?.byName ? ` · sent by ${data.lastHandoff.byName}` : ''}
          </span>
        )}
        {!data.escalated && data.lastHandoff?.direction === 'to_client' && (
          <span className="badge bg-blue-100 text-blue-700 dark:bg-blue-900/50 dark:text-blue-300" title={data.lastHandoff?.reason || ''}>
            Handed back by {data.lastHandoff.byName || 'your support provider'}
          </span>
        )}
      </div>
      <div className="card-body space-y-4">
        {error && (
          <div className="p-3 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-lg text-red-700 dark:text-red-300 text-sm flex items-start gap-2">
            <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
            <span className="flex-1">{error}</span>
            <button type="button" onClick={() => setError(null)} className="text-xs underline">dismiss</button>
          </div>
        )}

        {data.canWork && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="label">Working on it</label>
              <select
                value={data.assignee?.id || ''}
                disabled={busy}
                onChange={(e) => assign.mutate(e.target.value || null)}
                className="input w-full"
              >
                <option value="">Nobody yet</option>
                {data.technicians.map((t) => (
                  <option key={t.id} value={t.id}>{t.name}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="label">Status</label>
              {data.onIntake && !closed ? (
                <select
                  value=""
                  disabled={busy || data.statuses.length === 0}
                  onChange={(e) => e.target.value && status.mutate(Number(e.target.value))}
                  className="input w-full"
                >
                  <option value="">Change status…</option>
                  {data.statuses.map((s) => (
                    <option key={s.id} value={s.id}>{s.name}</option>
                  ))}
                </select>
              ) : (
                <p className="text-sm text-neutral-500 dark:text-neutral-400 py-2">
                  {closed ? 'Closed; reply to reopen.' : 'Your support provider sets the status while they have it.'}
                </p>
              )}
            </div>
          </div>
        )}

        <div className="flex flex-wrap gap-2">
          {data.canEscalate && !closed && (
            <button type="button" onClick={() => setShowEscalate((v) => !v)} disabled={busy} className="btn-secondary inline-flex items-center gap-1">
              <ArrowUpRight className="w-4 h-4" /> Escalate to your support provider
            </button>
          )}
          {data.canLogTime && (
            <button type="button" onClick={() => setShowTime((v) => !v)} disabled={busy} className="btn-secondary inline-flex items-center gap-1">
              <Timer className="w-4 h-4" /> Log time
            </button>
          )}
        </div>

        {showEscalate && (
          <div className="p-4 rounded-lg border border-neutral-200 dark:border-neutral-700 space-y-3">
            <p className="text-sm text-neutral-600 dark:text-neutral-300">
              The ticket moves to your support provider&apos;s queue with your reason. Your team can still read and add notes; they set the status from here on.
            </p>
            <textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={3} placeholder="What you tried, and what you need from them" className="input w-full" />
            <div className="flex justify-end gap-2">
              <button type="button" onClick={() => setShowEscalate(false)} className="btn-ghost">Cancel</button>
              <button type="button" onClick={() => escalate.mutate()} disabled={busy || !reason.trim()} className="btn-primary">
                {escalate.isPending ? 'Escalating…' : 'Escalate'}
              </button>
            </div>
          </div>
        )}

        {showTime && (
          <div className="p-4 rounded-lg border border-neutral-200 dark:border-neutral-700 space-y-3">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div>
                <label className="label">Hours</label>
                <input type="number" min="0.25" step="0.25" max="24" value={hours} onChange={(e) => setHours(e.target.value)} className="input w-full" placeholder="0.5" />
              </div>
              <div className="sm:col-span-2">
                <label className="label">What you did</label>
                <input type="text" value={timeNote} onChange={(e) => setTimeNote(e.target.value)} className="input w-full" placeholder="Optional" />
              </div>
            </div>
            <p className="text-xs text-neutral-500 dark:text-neutral-400">Recorded for your own team&apos;s records; it is not billed.</p>
            <div className="flex justify-end gap-2">
              <button type="button" onClick={() => setShowTime(false)} className="btn-ghost">Cancel</button>
              <button type="button" onClick={() => logTime.mutate()} disabled={busy || !(Number(hours) > 0)} className="btn-primary">
                {logTime.isPending ? 'Saving…' : 'Save time'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
